import { useState, useMemo } from 'react';
import { Target, Check, ChevronRight, Droplets, Sun, Sparkles, Waves, Zap, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useMacroGoals, MacroGoal, MicroGoal } from '@/hooks/useMacroGoals';
import { useSaveUserGoals } from '@/hooks/useUserMacroGoals';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface OnboardingSlideProps {
  onComplete: () => void;
  isCompleting: boolean;
  isLastSlide: boolean;
  onNext?: () => void;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Droplets,
  Sun,
  Sparkles,
  Waves,
  Zap,
  Heart,
};

const colorMap: Record<string, string> = {
  'text-blue-500': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  'text-purple-500': 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  'text-rose-500': 'bg-rose-500/10 text-rose-600 border-rose-500/30',
  'text-cyan-500': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
  'text-amber-500': 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  'text-red-400': 'bg-red-400/10 text-red-500 border-red-400/30',
};

const selectedColorMap: Record<string, string> = {
  'text-blue-500': 'bg-blue-500 text-white border-blue-500',
  'text-purple-500': 'bg-purple-500 text-white border-purple-500',
  'text-rose-500': 'bg-rose-500 text-white border-rose-500',
  'text-cyan-500': 'bg-cyan-500 text-white border-cyan-500',
  'text-amber-500': 'bg-amber-500 text-white border-amber-500',
  'text-red-400': 'bg-red-400 text-white border-red-400',
};

export const OnboardingSlideGoalsMacro = ({ onNext }: OnboardingSlideProps) => {
  const [step, setStep] = useState<'macro' | 'micro'>('macro');
  const [selectedMacroIds, setSelectedMacroIds] = useState<string[]>([]);
  const [selectedMicroIds, setSelectedMicroIds] = useState<string[]>([]);
  
  const { data: macroGoals, isLoading } = useMacroGoals();
  const saveGoals = useSaveUserGoals();

  // Get micro goals for selected macro goals
  const availableMicroGoals = useMemo(() => {
    if (!macroGoals) return [];
    return macroGoals
      .filter(macro => selectedMacroIds.includes(macro.id))
      .flatMap(macro => macro.micro_goals || []);
  }, [macroGoals, selectedMacroIds]);

  const toggleMacroGoal = (id: string) => {
    setSelectedMacroIds(prev => {
      if (prev.includes(id)) {
        // Remove and clear associated micro goals
        const macroGoal = macroGoals?.find(m => m.id === id);
        const microIdsToRemove = macroGoal?.micro_goals?.map(m => m.id) || [];
        setSelectedMicroIds(micro => micro.filter(mId => !microIdsToRemove.includes(mId)));
        return prev.filter(mId => mId !== id);
      }
      // Allow max 2 macro goals
      if (prev.length >= 2) {
        toast.info('You can select up to 2 focus areas');
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleMicroGoal = (id: string) => {
    setSelectedMicroIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(mId => mId !== id);
      }
      return [...prev, id];
    });
  };

  const handleContinueToMicro = () => {
    if (selectedMacroIds.length === 0) {
      toast.error('Please select at least one focus area');
      return;
    }
    setStep('micro');
  };

  const handleBack = () => {
    setStep('macro');
  };

  const handleSaveAndContinue = async () => {
    try {
      await saveGoals.mutateAsync({
        macroGoalIds: selectedMacroIds,
        microGoalIds: selectedMicroIds,
      });
      toast.success('Goals saved successfully!');
      onNext?.();
    } catch (error) {
      // Error handled in mutation
    }
  };

  const handleSkipMicro = async () => {
    try {
      await saveGoals.mutateAsync({
        macroGoalIds: selectedMacroIds,
        microGoalIds: [],
      });
      toast.success('Goals saved successfully!');
      onNext?.();
    } catch (error) {
      // Error handled in mutation
    }
  };

  if (isLoading) {
    return (
      <Card className="shadow-card border-0 bg-gradient-card">
        <CardContent className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">Loading goals...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // STEP 1: Macro Goal Selection
  if (step === 'macro') {
    return (
      <Card className="shadow-card border-0 bg-gradient-card">
        <CardContent className="p-6 md:p-8 flex flex-col">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-3 shadow-glow animate-scale-in">
              <Target className="w-8 h-8 text-primary-foreground" />
            </div>
            <h2 className="text-xl md:text-2xl font-semibold mb-2 animate-fade-in">
              What's your main skin focus?
            </h2>
            <p className="text-sm text-muted-foreground max-w-md animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Choose 1-2 areas you want to improve. We'll personalize everything based on your selection.
            </p>
          </div>

          {/* Macro Goals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {macroGoals?.map((goal, index) => {
              const isSelected = selectedMacroIds.includes(goal.id);
              const Icon = iconMap[goal.icon_name || 'Target'] || Target;
              const colorClass = goal.color_class || 'text-primary';
              
              return (
                <button
                  key={goal.id}
                  onClick={() => toggleMacroGoal(goal.id)}
                  className={cn(
                    'p-4 rounded-xl border-2 transition-all text-left animate-fade-in relative overflow-hidden',
                    isSelected
                      ? selectedColorMap[colorClass] || 'bg-primary text-primary-foreground border-primary'
                      : colorMap[colorClass] || 'bg-muted/50 border-border hover:border-primary/50'
                  )}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      isSelected ? 'bg-white/20' : 'bg-background'
                    )}>
                      <Icon className={cn('w-5 h-5', isSelected ? 'text-current' : colorClass)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm mb-0.5">{goal.label}</h3>
                      <p className={cn(
                        'text-xs line-clamp-2',
                        isSelected ? 'text-current/80' : 'text-muted-foreground'
                      )}>
                        {goal.description}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selection Counter */}
          <div className="text-center text-xs text-muted-foreground mb-4">
            {selectedMacroIds.length > 0 ? (
              <span>{selectedMacroIds.length} of 2 focus areas selected</span>
            ) : (
              <span>Select at least one focus area to continue</span>
            )}
          </div>

          {/* Actions */}
          <Button
            size="lg"
            onClick={handleContinueToMicro}
            disabled={selectedMacroIds.length === 0}
            className="w-full bg-gradient-primary hover:opacity-90"
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // STEP 2: Micro Goal Selection (Optional)
  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="p-6 md:p-8 flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-3 shadow-glow animate-scale-in">
            <Sparkles className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold mb-2 animate-fade-in">
            Refine your goals (optional)
          </h2>
          <p className="text-sm text-muted-foreground max-w-md animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Select specific goals to help us fine-tune your recommendations.
          </p>
        </div>

        {/* Micro Goals by Macro */}
        <div className="space-y-4 mb-6 max-h-[350px] overflow-y-auto px-1">
          {macroGoals?.filter(macro => selectedMacroIds.includes(macro.id)).map(macro => {
            const colorClass = macro.color_class || 'text-primary';
            
            return (
              <div key={macro.id} className="space-y-2">
                <h3 className={cn('text-sm font-medium', colorClass)}>
                  {macro.label}
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {macro.micro_goals?.map((micro, index) => {
                    const isSelected = selectedMicroIds.includes(micro.id);
                    
                    return (
                      <button
                        key={micro.id}
                        onClick={() => toggleMicroGoal(micro.id)}
                        className={cn(
                          'p-3 rounded-lg border transition-all text-left animate-fade-in',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 bg-background'
                        )}
                        style={{ animationDelay: `${index * 0.03}s` }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium">{micro.label}</span>
                          {isSelected && (
                            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selection Counter */}
        <div className="text-center text-xs text-muted-foreground mb-4">
          {selectedMicroIds.length > 0 ? (
            <span>{selectedMicroIds.length} specific goals selected</span>
          ) : (
            <span>Skip this step or select specific goals</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            onClick={handleSaveAndContinue}
            disabled={saveGoals.isPending}
            className="w-full bg-gradient-primary hover:opacity-90"
          >
            {saveGoals.isPending ? 'Saving...' : 'Save & Continue'}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={handleSkipMicro}
              disabled={saveGoals.isPending}
              className="flex-1"
            >
              Skip
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
